import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, ScrollRestoration } from "react-router";
import { L10nProvider } from "src/L10nProvider";
import { PassengerDetailsProvider } from "src/PassengerDetailsProvider";
import UserProvider from "src/UserProvider";
import { ApiConfigProvider } from "src/api/ApiConfigProvider";
import { AuthProvider } from "src/auth/contexts/AuthContext";
import { DefaultErrorBoundary } from "src/components/DefaultErrorBoundary/DefaultErrorBoundary";
import { TravelSearchSettingsProvider } from "src/domain/SegmentScreen/TravelSearchSettingsProvider";
import { FeatureProvider } from "src/feature/FeatureProvider";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";

const queryClient = new QueryClient();

export function ProviderLayout() {
  return (
    <StyleSheetManager
      enableVendorPrefixes
      shouldForwardProp={shouldForwardProp}
    >
      <L10nProvider>
        <QueryClientProvider client={queryClient}>
          <ScrollRestoration />
          <ApiConfigProvider>
            <AuthProvider>
              <UserProvider>
                <PassengerDetailsProvider>
                  <TravelSearchSettingsProvider>
                    <Suspense fallback={null}>
                      <ErrorBoundary
                        fallbackRender={() => (
                          <FeatureProvider>
                            <DefaultErrorBoundary defaultMessage>
                              <Outlet />
                            </DefaultErrorBoundary>
                          </FeatureProvider>
                        )}
                      >
                        <FeatureProvider>
                          <DefaultErrorBoundary defaultMessage>
                            <Outlet />
                          </DefaultErrorBoundary>
                        </FeatureProvider>
                      </ErrorBoundary>
                    </Suspense>
                  </TravelSearchSettingsProvider>
                </PassengerDetailsProvider>
              </UserProvider>
            </AuthProvider>
          </ApiConfigProvider>
        </QueryClientProvider>
      </L10nProvider>
    </StyleSheetManager>
  );
}

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: unknown) {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}
